import React, { Fragment } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { CarIcon, UsersIcon, RadarIcon, ReceiptIcon, GaugeIcon, BellRingIcon, PackageIcon, SendIcon, RouteIcon, MapIcon, LayersIcon, Settings2Icon, EyeIcon, ShieldCheckIcon, FileCheckIcon, TrendingUpIcon, TargetIcon, BikeIcon, TruckIcon, UserIcon, BoxIcon } from 'lucide-react';
import { SharjahPoliceEmblem } from '../SharjahPoliceEmblem';
import { LaptopFrame } from '../LaptopFrame';
const PAIR2_LOGO = 'https://cdn.magicpatterns.com/uploads/51QRCG1WJpxN6EwND9yhQF/Pair2_(2).svg';
const FMS_WEB = 'https://cdn.magicpatterns.com/uploads/7mS9PFY7VGXZNzGwrSF3Rj/image.png';
const FMS_APP = 'https://cdn.magicpatterns.com/uploads/qgvmz7JfwXaVx3XEaQcEmt/image.png';
const DMS_WEB = 'https://cdn.magicpatterns.com/uploads/5TD7DtuHV58AjRz8xQrknz/image.png';
const DMS_APP = 'https://cdn.magicpatterns.com/uploads/pAi55V3HJ5JrBkFPRiXpqb/image.png';
type Feature = {
  icon: BoxIcon;
  label: string;
};
const FMS_FEATURES: Feature[] = [{
  icon: CarIcon,
  label: 'المركبات'
}, {
  icon: UsersIcon,
  label: 'السائقون'
}, {
  icon: RadarIcon,
  label: 'التتبع المباشر'
}, {
  icon: ReceiptIcon,
  label: 'المخالفات والغرامات'
}, {
  icon: GaugeIcon,
  label: 'سلوك السائق'
}, {
  icon: BellRingIcon,
  label: 'التنبيهات'
}];
const DMS_FEATURES: Feature[] = [{
  icon: PackageIcon,
  label: 'الطلبات'
}, {
  icon: SendIcon,
  label: 'التوزيع'
}, {
  icon: RouteIcon,
  label: 'المسارات'
}, {
  icon: MapIcon,
  label: 'الرحلات'
}, {
  icon: Settings2Icon,
  label: 'العمليات'
}, {
  icon: LayersIcon,
  label: 'الموارد'
}];
const BENEFITS: Feature[] = [{
  icon: EyeIcon,
  label: 'رؤية لحظية شاملة'
}, {
  icon: ShieldCheckIcon,
  label: 'أمن وسلامة مرورية'
}, {
  icon: FileCheckIcon,
  label: 'امتثال أعلى'
}, {
  icon: TrendingUpIcon,
  label: 'كفاءة تشغيلية أفضل'
}, {
  icon: TargetIcon,
  label: 'اتخاذ قرارات أدق'
}];
const ENTITY_ICONS: BoxIcon[] = [CarIcon, BikeIcon, TruckIcon, CarIcon, UserIcon];
export function AboutSlide() {
  const reduce = useReducedMotion();
  const base = reduce ? {
    duration: 0
  } : undefined;
  return <section dir="rtl" className="relative w-screen h-screen overflow-hidden flex flex-col" style={{
    background: '#EDF1F4'
  }} data-id="element-123">
      {/* ========= TOP BAR ========= */}
      <motion.div initial={{
      opacity: 0,
      y: -14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={base ?? {
      duration: 0.8,
      delay: 0.1,
      ease: [0.22, 1, 0.36, 1]
    }} className="relative z-20 flex items-center justify-between pt-[clamp(1rem,2.5vh,2rem)] px-[clamp(1.5rem,4vw,4rem)]" data-id="element-124">
        <SharjahPoliceEmblem variant="light" data-id="element-125" />
        <img src={PAIR2_LOGO} alt="Pair²" className="h-[clamp(2.25rem,4.5vh,3.25rem)] w-auto" style={{
        filter: 'drop-shadow(0 4px 18px rgba(47,169,156,0.28))'
      }} draggable={false} data-id="element-126" />
      </motion.div>

      {/* ========= MAIN BODY ========= */}
      <div className="relative z-10 flex-1 min-h-0 grid gap-[clamp(1rem,2vw,1.75rem)] px-[clamp(1.5rem,4vw,4rem)] pt-[clamp(0.5rem,1.5vh,1rem)]" style={{
      gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.5fr)'
    }} data-id="element-127">
        {/* ============ RIGHT (first in RTL) — Content ============ */}
        <motion.div initial={{
        opacity: 0,
        x: -16
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={base ?? {
        duration: 0.9,
        delay: 0.3
      }} className="min-w-0 flex flex-col justify-center" data-id="element-128">
          {/* Title */}
          <h2 className="font-arabic-display font-black leading-[1.05] text-right" style={{
          color: '#0B1328',
          fontSize: 'clamp(2rem, 4vw, 3.75rem)',
          letterSpacing: '-0.015em'
        }} data-id="element-129">
            نبذة عن{' '}
            <span style={{
            color: '#2FA99C'
          }} data-id="element-130">
              Pair²
            </span>
          </h2>

          {/* Teal underline */}
          <div className="h-[3px] mt-3 mr-0 ml-auto rounded-full" style={{
          background: '#2FA99C',
          width: 'clamp(3.5rem, 6vw, 5rem)'
        }} data-id="element-131" />

          {/* Paragraph */}
          <p className="font-arabic-body mt-[clamp(1.25rem,3vh,2rem)] leading-[2] text-right" style={{
          color: '#1A1F2B',
          fontSize: 'clamp(0.9rem, 1.1vw, 1.1rem)'
        }} data-id="element-132">
            تعتبر{' '}
            <span className="font-bold" style={{
            color: '#2FA99C'
          }} data-id="element-133">
              Pair²
            </span>{' '}
            شركة تكنولوجيا مقرها دولة الإمارات العربية المتحدة، متخصصة في تطوير
            منصات ذكية لإدارة النقل والأساطيل.
          </p>

          {/* Systems list */}
          <p className="font-arabic-body font-bold mt-[clamp(1rem,2.5vh,1.75rem)] text-right" style={{
          color: '#0B1328',
          fontSize: 'clamp(0.95rem, 1.1vw, 1.1rem)'
        }} data-id="element-134">
            توفر الشركة نظامين رئيسيين:
          </p>
          <div className="mt-2 space-y-2" data-id="element-135">
            {[{
            ar: 'نظام إدارة الأساطيل',
            en: 'FMS'
          }, {
            ar: 'نظام إدارة العمليات والتوزيع',
            en: 'DMS'
          }].map(s => <div key={s.en} className="flex items-center justify-end gap-3" data-id="element-136">
                <span className="font-arabic-body" style={{
              color: '#1A1F2B',
              fontSize: 'clamp(0.88rem, 1.05vw, 1.05rem)'
            }} data-id="element-137">
                  {s.ar}{' '}
                  <span style={{
                color: '#2FA99C',
                fontWeight: 600
              }} dir="ltr" data-id="element-138">
                    ({s.en})
                  </span>
                </span>
                <span className="inline-block w-2 h-2 rounded-full" style={{
              background: '#2FA99C'
            }} data-id="element-139" />
              </div>)}
          </div>

          {/* Conclusion */}
          <p className="font-arabic-body mt-[clamp(1rem,2.5vh,1.75rem)] leading-[2] text-right" style={{
          color: 'rgba(26,31,43,0.82)',
          fontSize: 'clamp(0.88rem, 1.05vw, 1.05rem)'
        }} data-id="element-140">
            وتهدف الشركة إلى تعزيز الرؤية اللحظية، دعم الأمن والسلامة المرورية،
            تحسين الامتثال، ورفع كفاءة اتخاذ القرار من خلال بيانات دقيقة
            ومباشرة.
          </p>

          {/* Entity icons row */}
          <div className="flex items-center justify-center gap-[clamp(1.25rem,2.5vw,2rem)] mt-[clamp(1.5rem,3.5vh,2.5rem)]" data-id="element-141">
            {ENTITY_ICONS.map((Icon, i) => <div key={i} className="rounded-full flex items-center justify-center" style={{
            width: 'clamp(3rem, 4.5vw, 4rem)',
            height: 'clamp(3rem, 4.5vw, 4rem)',
            background: 'rgba(47,169,156,0.08)',
            border: '1.5px solid rgba(47,169,156,0.25)'
          }} data-id="element-142">
                <Icon style={{
              width: 'clamp(1.5rem, 2.2vw, 2rem)',
              height: 'clamp(1.5rem, 2.2vw, 2rem)',
              color: '#2FA99C'
            }} strokeWidth={1.4} data-id="element-143" />
              </div>)}
          </div>
        </motion.div>

        {/* ============ LEFT (second in RTL) — FMS + DMS product cards ============ */}
        <motion.div initial={{
        opacity: 0,
        x: 16
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={base ?? {
        duration: 0.9,
        delay: 0.45
      }} className="grid gap-[clamp(0.75rem,1.5vh,1.25rem)] min-h-0" style={{
        gridTemplateRows: 'minmax(0,1.15fr) minmax(0,1fr)'
      }} data-id="element-144">
          <ProductCard acronym="FMS" titleEn="Fleet Management System" titleAr="نظام إدارة الأساطيل" webImage={FMS_WEB} appImage={FMS_APP} features={FMS_FEATURES} delay={0.6} data-id="element-145" />
          <ProductCard acronym="DMS" titleEn="Dispatch Management System" titleAr="نظام إدارة العمليات والتوزيع" webImage={DMS_WEB} appImage={DMS_APP} features={DMS_FEATURES} delay={0.8} data-id="element-146" />
        </motion.div>
      </div>

      {/* ========= BOTTOM BENEFITS STRIP ========= */}
      <motion.div initial={{
      opacity: 0,
      y: 14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={base ?? {
      duration: 0.9,
      delay: 1.05
    }} className="relative z-10 px-[clamp(1.5rem,4vw,4rem)] py-[clamp(0.75rem,1.5vh,1.25rem)]" data-id="element-147">
        <div dir="rtl" className="flex items-center justify-between gap-[clamp(0.5rem,1.2vw,1.25rem)] rounded-full px-[clamp(1rem,2vw,2rem)] py-[clamp(0.6rem,1.2vh,0.9rem)]" style={{
        background: 'rgba(255,255,255,0.7)',
        border: '1px solid rgba(26,31,43,0.08)',
        backdropFilter: 'blur(14px)',
        boxShadow: '0 6px 24px -10px rgba(14,93,85,0.1)'
      }} data-id="element-148">
          {BENEFITS.map(({
          icon: Icon,
          label
        }, i) => <Fragment key={i} data-id="element-149">
              <div className="flex items-center gap-2 min-w-0" dir="rtl" data-id="element-150">
                <div className="shrink-0 rounded-full flex items-center justify-center" style={{
              width: 'clamp(1.6rem, 2.2vw, 2.1rem)',
              height: 'clamp(1.6rem, 2.2vw, 2.1rem)',
              background: 'rgba(47,169,156,0.1)',
              border: '1px solid rgba(47,169,156,0.3)'
            }} data-id="element-151">
                  <Icon className="w-[clamp(0.8rem,1vw,1rem)] h-[clamp(0.8rem,1vw,1rem)]" style={{
                color: '#0E5D55'
              }} strokeWidth={2} data-id="element-152" />
                </div>
                <span className="font-arabic-body whitespace-nowrap text-right" style={{
              color: '#0B1328',
              fontSize: 'clamp(0.72rem, 0.9vw, 0.9rem)',
              fontWeight: 600
            }} data-id="element-153">
                  {label}
                </span>
              </div>
              {i < BENEFITS.length - 1 && <span className="h-4 w-px shrink-0" style={{
            background: 'rgba(26,31,43,0.12)'
          }} data-id="element-154" />}
            </Fragment>)}
        </div>
      </motion.div>
    </section>;
}
/* ==========================================================
   Product Card — badge + title + phone(left) + laptop(center) + features(right)
   ========================================================== */
function ProductCard({
  acronym,
  titleEn,
  titleAr,
  webImage,
  appImage,
  features,
  delay = 0
}: {
  acronym: string;
  titleEn: string;
  titleAr: string;
  webImage: string;
  appImage: string;
  features: Feature[];
  delay?: number;
}) {
  const reduce = useReducedMotion();
  const base = reduce ? {
    duration: 0
  } : undefined;
  return <motion.div initial={{
    opacity: 0,
    y: 14
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={base ?? {
    duration: 0.9,
    delay,
    ease: [0.22, 1, 0.36, 1]
  }} className="relative rounded-[clamp(0.75rem,1.2vw,1.1rem)] overflow-hidden min-h-0 flex flex-col" style={{
    background: 'rgba(255,255,255,0.75)',
    border: '1px solid rgba(26,31,43,0.08)',
    boxShadow: '0 12px 40px -18px rgba(14,93,85,0.18)'
  }} dir="ltr" data-id="element-155">
      {/* Header: badge + titles */}
      <div className="flex items-center gap-3 px-[clamp(0.75rem,1.4vw,1.25rem)] pt-[clamp(0.6rem,1.2vh,0.9rem)] pb-[clamp(0.3rem,0.8vh,0.6rem)]" data-id="element-156">
        <div className="shrink-0 rounded-lg flex items-center justify-center font-bold" style={{
        width: 'clamp(2.5rem, 3.5vw, 3.25rem)',
        height: 'clamp(1.75rem, 2.5vh, 2.25rem)',
        background: '#2FA99C',
        color: '#FFFFFF',
        fontSize: 'clamp(0.8rem, 1vw, 1rem)',
        letterSpacing: '0.08em'
      }} data-id="element-157">
          {acronym}
        </div>
        <div className="min-w-0" data-id="element-158">
          <div className="font-semibold" style={{
          color: '#0B1328',
          fontSize: 'clamp(0.82rem, 1.1vw, 1.05rem)'
        }} data-id="element-159">
            {titleEn}
          </div>
          <div className="font-arabic-body" dir="rtl" style={{
          color: 'rgba(26,31,43,0.7)',
          fontSize: 'clamp(0.72rem, 0.9vw, 0.88rem)'
        }} data-id="element-160">
            {titleAr}
          </div>
        </div>
      </div>

      {/* Body: phone | laptop | features */}
      <div className="relative flex-1 min-h-0 flex items-stretch px-[clamp(0.5rem,1vw,0.9rem)] pb-[clamp(0.5rem,1vh,0.75rem)]" data-id="element-161">
        {/* Phone mockup — left side, overlapping slightly */}
        <div className="relative shrink-0 flex items-center" style={{
        width: 'clamp(5rem, 9vw, 7.5rem)',
        zIndex: 2
      }} data-id="element-162">
          <img src={appImage} alt={`${titleEn} app`} draggable={false} className="w-full h-auto max-h-full object-contain select-none" style={{
          filter: 'drop-shadow(0 18px 30px rgba(14,93,85,0.22)) drop-shadow(0 6px 14px rgba(26,31,43,0.15))'
        }} data-id="element-163" />
        </div>

        {/* Laptop — center */}
        <div className="relative flex-1 min-w-0 flex items-center -ml-[clamp(0.5rem,1vw,1rem)]" style={{
        zIndex: 1,
        maxWidth: 'clamp(12rem, 20vw, 18rem)'
      }} data-id="element-164">
          <LaptopFrame src={webImage} alt={`${titleEn} dashboard`} className="w-full" data-id="element-165" />
        </div>

        {/* Feature list — right side, vertical */}
        <div className="shrink-0 flex flex-col justify-center gap-[clamp(0.35rem,0.7vh,0.55rem)] pl-[clamp(0.5rem,0.8vw,0.75rem)]" style={{
        width: 'clamp(9rem, 15vw, 13rem)'
      }} data-id="element-166">
          {features.map(({
          icon: Icon,
          label
        }, i) => <div key={i} className="flex items-center gap-[clamp(0.4rem,0.7vw,0.6rem)] justify-end" data-id="element-167">
              <span className="font-arabic-body whitespace-nowrap" style={{
            color: '#1A1F2B',
            fontSize: 'clamp(0.7rem, 0.88vw, 0.88rem)',
            fontWeight: 500
          }} data-id="element-168">
                {label}
              </span>
              <div className="shrink-0 rounded-full flex items-center justify-center" style={{
            width: 'clamp(1.6rem, 2.2vw, 2rem)',
            height: 'clamp(1.6rem, 2.2vw, 2rem)',
            background: 'rgba(47,169,156,0.08)',
            border: '1.5px solid rgba(47,169,156,0.35)'
          }} data-id="element-169">
                <Icon style={{
              width: 'clamp(0.78rem, 1vw, 0.95rem)',
              height: 'clamp(0.78rem, 1vw, 0.95rem)',
              color: '#2FA99C'
            }} strokeWidth={1.8} data-id="element-170" />
              </div>
            </div>)}
        </div>
      </div>
    </motion.div>;
}